import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import { FooterComponents } from "../FooterComponent";
import { getToken } from '../../utils/auth';
import { BarChart3, Users, FileText, Award, Download, Filter, Search, Eye, ClipboardCheck } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default function EvaluatorReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedScholarship, setSelectedScholarship] = useState("all");
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatistics();
  }, []);

  useEffect(() => {
    filterStatistics();
  }, [searchTerm, selectedScholarship, statistics]);

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}hec/abroadSelectionMarking/evaluator-statistics`,
        {
          headers: {
            Authorization: getToken() ? `Bearer ${getToken()}` : undefined,
          },
        }
      );
      setStatistics(response.data || []);
      setFilteredStats(response.data || []);
    } catch (err) {
      setError("Failed to fetch evaluator statistics");
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  const filterStatistics = () => {
    let filtered = [...statistics];

    // Filter by search term (evaluator name or program name)
    if (searchTerm) {
      filtered = filtered.filter(
        (stat) =>
          stat.evaluatorFullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          stat.programName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by scholarship
    if (selectedScholarship !== "all") {
      filtered = filtered.filter((stat) => stat.scholarshipId === selectedScholarship);
    }

    setFilteredStats(filtered);
  };

  const getUniqueScholarships = () => {
    const scholarships = statistics.reduce((acc, stat) => {
      if (!acc.find((s) => s.id === stat.scholarshipId)) {
        acc.push({
          id: stat.scholarshipId,
          name: stat.scholarshipName,
        });
      }
      return acc;
    }, []);
    return scholarships;
  };

  const getTotalApplicantsEvaluated = () => {
    return filteredStats.reduce((sum, stat) => sum + stat.numberOfApplicantsEvaluated, 0);
  };

  const getTotalEvaluators = () => {
    const uniqueEvaluators = new Set(filteredStats.map((stat) => stat.evaluatorId));
    return uniqueEvaluators.size;
  };

  const getTotalPrograms = () => {
    const uniquePrograms = new Set(filteredStats.map((stat) => stat.programId));
    return uniquePrograms.size;
  };

  const handleExportToExcel = () => {
    const excelData = filteredStats.map((stat, index) => ({
      "#": index + 1,
      "Evaluator Name": stat.evaluatorFullName,
      "Scholarship": stat.scholarshipName,
      "Program": stat.programName,
      "Applicants Evaluated": stat.numberOfApplicantsEvaluated,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Evaluator Statistics");

    // Set column widths
    worksheet["!cols"] = [
      { wch: 5 },
      { wch: 30 },
      { wch: 50 },
      { wch: 30 },
      { wch: 20 },
    ];

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Evaluator_Statistics_${new Date().toISOString().split("T")[0]}.xlsx`);

    toast.success("Report exported successfully!", {
      position: "top-center",
      autoClose: 3000,
      theme: "colored",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBarComponent />
        <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
          <SidebarComponent />
        </aside>
        <div className="pt-6 sm:ml-64">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-16 h-16 bg-hec-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <BarChart3 className="w-8 h-8 text-hec-600" />
              </div>
              <p className="text-hec-600 text-lg">Loading statistics...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBarComponent />
        <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
          <SidebarComponent />
        </aside>
        <div className="pt-6 sm:ml-64">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 text-lg">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBarComponent />
      <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
        <SidebarComponent />
      </aside>

      <div className="pt-6 sm:ml-64">
        <div className="p-6 min-h-screen">
          <div className="py-8 mt-10">
            {/* Header */}
            <div className="mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-hec-600 to-hec-700 rounded-2xl flex items-center justify-center shadow-lg">
                      <BarChart3 className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-hec-700">Evaluator Statistics Report</h1>
                      <p className="text-hec-600 mt-1">Overview of evaluator performance and activities</p>
                    </div>
                  </div>
                  <button
                    onClick={handleExportToExcel}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg"
                  >
                    <Download className="w-5 h-5" />
                    <span>Export to Excel</span>
                  </button>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Total Evaluators</p>
                        <p className="text-3xl font-bold text-blue-700 mt-1">{getTotalEvaluators()}</p>
                      </div>
                      <Users className="w-12 h-12 text-blue-500 opacity-50" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Total Programs</p>
                        <p className="text-3xl font-bold text-purple-700 mt-1">{getTotalPrograms()}</p>
                      </div>
                      <Award className="w-12 h-12 text-purple-500 opacity-50" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Total Applications Evaluated</p>
                        <p className="text-3xl font-bold text-green-700 mt-1">{getTotalApplicantsEvaluated()}</p>
                      </div>
                      <FileText className="w-12 h-12 text-green-500 opacity-50" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6 mb-6">
              <div className="flex items-center space-x-2 mb-4">
                <Filter className="w-5 h-5 text-hec-700" />
                <h2 className="text-xl font-semibold text-hec-700">Filters</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Search className="w-4 h-4 inline mr-2" />
                    Search by Evaluator or Program
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Type to search..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-hec-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Award className="w-4 h-4 inline mr-2" />
                    Filter by Scholarship
                  </label>
                  <select
                    value={selectedScholarship}
                    onChange={(e) => setSelectedScholarship(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-hec-500"
                  >
                    <option value="all">All Scholarships</option>
                    {getUniqueScholarships().map((scholarship) => (
                      <option key={scholarship.id} value={scholarship.id}>
                        {scholarship.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Statistics Table */}
            <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6">
              <h2 className="text-xl font-semibold text-hec-700 mb-4">Detailed Statistics</h2>
              
              {filteredStats.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No statistics found matching your filters.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-hec-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">#</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">Evaluator Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">Scholarship</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">Program</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-hec-700 uppercase tracking-wider">Applicants Evaluated</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-hec-700 uppercase tracking-wider" colSpan="2">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredStats.map((stat, index) => (
                        <tr key={`${stat.evaluatorId}-${stat.programId}`} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-hec-100 rounded-full flex items-center justify-center mr-3">
                                <Users className="w-5 h-5 text-hec-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{stat.evaluatorFullName}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {stat.scholarshipName}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{stat.programName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-hec-100 text-hec-700">
                              {stat.numberOfApplicantsEvaluated}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => navigate(`/evaluator-marking-details/${stat.evaluatorId}/${stat.programId}`)}
                              className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors duration-200 shadow-sm mr-2"
                            >
                              <ClipboardCheck className="w-4 h-4 mr-2" />
                              View Evaluated
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => navigate(`/unevaluated-applications/${stat.evaluatorId}/${stat.programId}`)}
                              className="inline-flex items-center px-4 py-2 bg-hec-600 hover:bg-hec-700 text-white text-xs font-medium rounded-lg transition-colors duration-200 shadow-sm"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Unevaluated
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <FooterComponents />
      </div>
    </div>
  );
}
